import { getIframeToken, logoutUser } from "@/services/user";
import { RootState } from "@/store";
import { useCheckAdditionalDataRequirementQuery } from "@/store/userAccountApiSlice";
import { AlertCircle } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Button from "../Buttons/Button";
import { customToast } from "../Toast";
import Modal from "./index";

interface AdditionalDataModalProps {
  onOpenIframe: (token: string) => void;
}

const AdditionalDataModal: React.FC<AdditionalDataModalProps> = ({
  onOpenIframe,
}) => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user);
  const [isFetchingToken, setIsFetchingToken] = useState(false);

  const isAuthenticated = !!user.id;

  const { data, isLoading } = useCheckAdditionalDataRequirementQuery(
    undefined,
    {
      skip: !isAuthenticated,
    },
  );

  const handleVerify = async () => {
    try {
      setIsFetchingToken(true);
      const { token } = await getIframeToken();

      if (!token) {
        customToast("Unable to retrieve user token", "error");
        return;
      }

      onOpenIframe(token);
    } catch {
      customToast("Unable to retrieve user token", "error");
    } finally {
      setIsFetchingToken(false);
    }
  };

  const isOpen = isAuthenticated && !isLoading && !!data?.isBannerRequired;

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      widthSize="md:w-[480px]"
      contentClassName="additional-data-modal !z-[999999999]"
      containerClassName="!z-[9999999]"
      // Blocking modal — backdrop clicks must not dismiss it. The user has to
      // either verify their information or log out.
      onCancel={() => {}}
    >
      <div className="flex flex-col rounded-[24px] overflow-hidden">
        <div className="flex flex-col items-center gap-3 px-[24px] pt-[32px] pb-[16px] text-center">
          <AlertCircle
            style={{ color: window.partner?.colors?.primary }}
            className="w-10 h-10 text-brand-default"
          />
          <h3 className="text-[18px] font-semibold font-outfit">
            {t("additional_data_collection.action_required")}
          </h3>
        </div>

        <div className="bg-[#F9F9F9] px-[24px] py-[20px] text-center">
          <p className="text-[16px] text-black leading-[1.5]">
            {t("additional_data_collection.description")}
          </p>
        </div>

        <div className="flex items-center gap-[15px] px-[24px] py-[16px] bg-white">
          <Button
            size="small"
            variant="secondary"
            iconVisibility="hide"
            ariaLabel="Logout"
            label={t("additional_data_collection.logout_button")}
            handleClick={logoutUser}
          />

          <Button
            size="small"
            variant="primary"
            iconVisibility="hide"
            ariaLabel="Verify information"
            loading={isFetchingToken}
            loadingText={t("additional_data_collection.button_text")}
            label={t("additional_data_collection.button_text")}
            handleClick={handleVerify}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AdditionalDataModal;
